// Shared presentation layer for the company billing surface — Subscribe page,
// Billing tab, invoice/topup panels, and the printable invoice document. Single
// source of truth for Rupiah formatting + payment-status vocabulary, plus the
// "does this company owe a payment?" derivation that powers the DetailSlider
// alert banner (and, later, the company-list badge).

export function formatIDR(value) {
  const n = Number(value || 0)
  return `Rp ${n.toLocaleString('id-ID')}`
}

// Invoice payment lifecycle (Xendit-backed).
export const INVOICE_STATUS_BADGE = {
  unpaid: { cls: 'bg-warning/50 border-warning/200 text-warning/700', label: 'Unpaid' },
  paid: { cls: 'bg-success/50 border-success/200 text-success/700', label: 'Paid' },
  canceled: { cls: 'bg-error/50 border-error/200 text-error/700', label: 'Canceled' },
  expired: { cls: 'bg-gray-light/100 border-gray-light/300 text-gray-light/700', label: 'Expired' },
}

// Subscription lifecycle (SAW-authoritative).
export const SUBSCRIPTION_STATUS_BADGE = {
  active: { cls: 'bg-success/50 border-success/200 text-success/700', label: 'Active' },
  pending_payment: {
    cls: 'bg-warning/50 border-warning/200 text-warning/700',
    label: 'Pending payment',
  },
  suspended: { cls: 'bg-warning/50 border-warning/200 text-warning/700', label: 'Suspended' },
  canceled: { cls: 'bg-error/50 border-error/200 text-error/700', label: 'Canceled' },
  expired: { cls: 'bg-gray-light/100 border-gray-light/300 text-gray-light/700', label: 'Expired' },
}

const FALLBACK_BADGE = { cls: 'bg-gray-light/50 border-gray-light/200 text-gray-light/700', label: '' }

export function invoiceStatusBadge(status) {
  return INVOICE_STATUS_BADGE[status] || { ...FALLBACK_BADGE, label: status || '' }
}

export function subscriptionStatusBadge(status) {
  return SUBSCRIPTION_STATUS_BADGE[status] || { ...FALLBACK_BADGE, label: status || '' }
}

// An invoice is still actionable (Xendit checkout reachable) only while unpaid.
export const isPayableInvoice = (inv) => inv?.status === 'unpaid'

// Reduce a company's subscription + invoice list to a single actionable payment
// alert, or null when nothing is owed. `severity`: 'error' = blocking state
// (suspended → enquiry disabled), 'warning' = pending / outstanding. `invoiceId`
// is what "Pay now" jumps to: the oldest unpaid invoice, preferring the
// platform-fee invoice that gates activation when pending the first payment.
export function derivePaymentAlert({ subscription, invoices } = {}) {
  const unpaid = (Array.isArray(invoices) ? invoices : []).filter(isPayableInvoice)
  const byOldest = [...unpaid].sort(
    (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0),
  )
  const subStatus = subscription?.status

  if (subStatus === 'suspended') {
    const target = byOldest[0] || null
    return {
      severity: 'error',
      title: 'Subscription suspended',
      message: 'You have an overdue invoice — pay it to re-enable enquiries.',
      invoiceId: target?.id || null,
      amountIdr: target?.total_idr ?? null,
      count: unpaid.length,
    }
  }

  if (subStatus === 'pending_payment') {
    const subInvoice =
      byOldest.find((i) => i.invoice_type === 'subscription') || byOldest[0] || null
    return {
      severity: 'warning',
      title: 'Awaiting first payment',
      message: 'Pay the platform fee to activate your subscription. Quota & enquiries stay disabled until it is paid.',
      invoiceId: subInvoice?.id || null,
      amountIdr: subInvoice?.total_idr ?? null,
      count: unpaid.length,
    }
  }

  if (unpaid.length > 0) {
    const target = byOldest[0]
    return {
      severity: 'warning',
      title: unpaid.length > 1 ? `${unpaid.length} unpaid invoices` : 'Unpaid invoice',
      message: 'Complete the payment before the due date.',
      invoiceId: target?.id || null,
      amountIdr: unpaid.length === 1 ? (target?.total_idr ?? null) : null,
      count: unpaid.length,
    }
  }

  return null
}
